import React, { useState } from 'react';
import { startOfDay, endOfDay } from 'date-fns';
import { Button, FormControl, InputAdornment, Select, TextField } from '@material-ui/core';
import { DatePicker, DateTimePicker } from '@material-ui/pickers';
import SearchIcon from '@material-ui/icons/Search';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { IconButton } from '@material-ui/core';

import { IconInputWrapper, InnerAdornment, OuterAdornment } from '$styles/input';
import { Inline, SPACE, Space } from '$gstyles';
import { DATE_FORMATS } from '$gbusiness/enums';
import dateRangeOptions, { KEYS, DATE_RANGE_TYPE } from '$business/enums/options/dateRange';
import { input } from '$gcomponents/utils';
import { getDateRangeFromPeriod } from '$business/helpers/util';
import intl from '$intl';

interface LeftComponentProps {
  onSearch: Function;
  useDatetime?: boolean;
  parentFilter?: filterProps;
  defaultRange?: DATE_RANGE_TYPE;
}

export interface filterProps {
  query: string;
  dateStart: Date | null;
  dateEnd: Date | null;
  forceRefresh?: boolean;
}

export const defaultFilter: filterProps = {
  query: '',
  dateStart: null,
  dateEnd: null,
  forceRefresh: false,
};

const LeftComponent: React.FC<LeftComponentProps> = ({
  parentFilter,
  onSearch,
  defaultRange = KEYS.CUSTOM,
  useDatetime = false,
}) => {
  const [filter, setFilter] = useState({ ...defaultFilter, ...parentFilter });
  const [rangeSelection, setRangeSelection] = useState(defaultRange);
  const onQueryChange = e => {
    setFilter({ ...filter, query: e.target.value });
  };

  const onRangeSelectionChange = e => {
    const value = e.target.value;
    if (value === KEYS.CUSTOM) return;
    setRangeSelection(value);
    const [dateStart, dateEnd] = getDateRangeFromPeriod(value);
    setFilter({ ...filter, dateStart: dateStart, dateEnd: dateEnd });
  };
  const onDateStartChange = e => {
    setFilter({ ...filter, dateStart: useDatetime ? e : startOfDay(e) });
    setRangeSelection(KEYS.CUSTOM);
  };
  const onDateEndChange = e => {
    setFilter({ ...filter, dateEnd: useDatetime ? e : endOfDay(e) });
    setRangeSelection(KEYS.CUSTOM);
  };
  const searchNow = e => {
    e.stopPropagation();
    onSearch(filter);
  };
  const resetFilter = () => {
    setRangeSelection(defaultRange);
    setFilter(defaultFilter);
  };

  const Picker = useDatetime ? DateTimePicker : DatePicker;
  const dateFormat = useDatetime ? DATE_FORMATS.DATETIME : DATE_FORMATS.SLASH;
  const pickerWidth = useDatetime ? 210 : 140;
  const rangeWidth = 150;
  const dateRanges = input.generateSelectOptions(dateRangeOptions, true);

  return (
    <>
      <IconInputWrapper>
        <TextField
          label=""
          variant="outlined"
          placeholder={intl('INPUT.PLACEHOLDER.SEARCH')}
          size="small"
          style={{ minWidth: pickerWidth + 'px' }}
          value={filter.query}
          onChange={onQueryChange}
          onKeyPress={ev => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              searchNow(ev);
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={searchNow}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </IconInputWrapper>
      <Space.Hor margin={SPACE.MEDIUM} />
      <FormControl variant="outlined" size="small" style={{ minWidth: rangeWidth + 'px' }}>
        <Select displayEmpty variant="outlined" onChange={onRangeSelectionChange} value={rangeSelection}>
          {dateRanges}
        </Select>
      </FormControl>
      <IconInputWrapper>
        <Picker
          clearable
          label=""
          size="small"
          inputVariant="outlined"
          value={filter.dateStart}
          onChange={onDateStartChange}
          maxDate={filter.dateEnd}
          format={dateFormat}
          style={{ width: pickerWidth + 'px' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <InnerAdornment>
                  <CalendarTodayIcon />
                </InnerAdornment>
              </InputAdornment>
            ),
          }}
        />
      </IconInputWrapper>
      <Inline className="flexCenter">to</Inline>
      <IconInputWrapper>
        <Picker
          clearable
          label=""
          size="small"
          inputVariant="outlined"
          value={filter.dateEnd}
          onChange={onDateEndChange}
          minDate={filter.dateStart}
          style={{ width: pickerWidth + 48 + 'px' }}
          format={dateFormat}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <InnerAdornment>
                  <CalendarTodayIcon />
                </InnerAdornment>
                <OuterAdornment onClick={searchNow}>
                  <SearchIcon />
                </OuterAdornment>
              </InputAdornment>
            ),
          }}
        />
      </IconInputWrapper>
      <Button size="small" color="primary" onClick={resetFilter}>
        {intl('COMMON.RESET')}
      </Button>
    </>
  );
};

export default LeftComponent;
